import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { api } from '../services/api';
import type { Agent, LoginRequest } from '../types';
import { shouldAutoLogout, formatSaoPauloTime } from '../utils/timezone';
import { io, Socket } from 'socket.io-client';

interface AuthContextType {
  agent: Agent | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
  updateAgent: (agent: Agent) => void;
  isAdmin: () => boolean;
  isDepartmentManager: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [agent, setAgent] = useState<Agent | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const businessHoursCheckInterval = useRef<number | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const [autoLogoutEnabled, setAutoLogoutEnabled] = useState(false);
  const [autoLogoutHour, setAutoLogoutHour] = useState(20);
  const isManualLogout = useRef(false);

  // Load auto-logout settings from backend (only if authenticated)
  useEffect(() => {
    const loadAutoLogoutSettings = async () => {
      // Only load settings if user is authenticated
      const token = localStorage.getItem('token');
      if (!token) {
        return;
      }

      try {
        const settings = await api.getSettings();
        setAutoLogoutEnabled(settings.autoLogoutEnabled === 'true' || settings.autoLogoutEnabled === true);
        setAutoLogoutHour(parseInt(settings.autoLogoutHour || '20', 10));
      } catch (error) {
        console.error('Failed to load auto-logout settings:', error);
        // Use defaults on error
        setAutoLogoutEnabled(false);
        setAutoLogoutHour(20);
      }
    };

    loadAutoLogoutSettings();
  }, []);

  // Check if should auto-logout based on business hours
  const checkBusinessHours = () => {
    // Only check if auto-logout is enabled
    if (!autoLogoutEnabled) {
      return;
    }

    const lastLoginTime = localStorage.getItem('lastLoginTime');
    if (!lastLoginTime) {
      return;
    }

    const lastLoginTimestamp = parseInt(lastLoginTime, 10);
    if (shouldAutoLogout(lastLoginTimestamp, autoLogoutHour)) {
      console.log(
        `[Auto Logout] Logging out at ${formatSaoPauloTime()} - End of business hours (${autoLogoutHour}:00 São Paulo time)`
      );
      logout();
    }
  };

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('token');
    const storedAgent = localStorage.getItem('agent');

    if (token && storedAgent) {
      try {
        setAgent(JSON.parse(storedAgent));

        // Check immediately on load if should auto-logout
        checkBusinessHours();
      } catch (error) {
        console.error('Failed to parse stored agent:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('agent');
        localStorage.removeItem('lastLoginTime');
      }
    }
    setIsLoading(false);
  }, []);

  // Periodic check for business hours (every 1 minute)
  useEffect(() => {
    if (agent && autoLogoutEnabled) {
      // Check every minute if we should auto-logout
      businessHoursCheckInterval.current = setInterval(() => {
        checkBusinessHours();
      }, 60000); // 60 seconds

      return () => {
        if (businessHoursCheckInterval.current) {
          clearInterval(businessHoursCheckInterval.current);
        }
      };
    }
  }, [agent, autoLogoutEnabled, autoLogoutHour]);

  // WebSocket listener for forced logout when backend logs out the user
  useEffect(() => {
    if (!agent) {
      // User not logged in, don't connect WebSocket
      return;
    }

    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
    console.log('[AuthContext] Connecting WebSocket for forced logout detection');

    const socket = io(apiUrl, {
      transports: ['polling', 'websocket'],
      upgrade: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('[AuthContext] WebSocket connected:', socket.id);
    });

    // Listen for agent status updates
    socket.on('agent-status-updated', (data: { agentId: string; status: string }) => {
      console.log('[AuthContext] Agent status updated:', data);

      // Check if it's the current user and status is OFFLINE
      if (data.agentId === agent.id && data.status === 'OFFLINE') {
        // Only show alert and force logout if it wasn't a manual logout
        if (!isManualLogout.current) {
          console.log('[AuthContext] Current user was logged out by backend, forcing logout...');
          alert('Você foi deslogado automaticamente pelo sistema.');
          logout();
        } else {
          console.log('[AuthContext] Manual logout detected, skipping alert');
          isManualLogout.current = false; // Reset flag
        }
      }
    });

    socket.on('disconnect', () => {
      console.log('[AuthContext] WebSocket disconnected');
    });

    socket.on('error', (error: any) => {
      console.error('[AuthContext] WebSocket error:', error);
    });

    // Cleanup on unmount or agent change
    return () => {
      console.log('[AuthContext] Cleaning up WebSocket connection');
      socket.off('agent-status-updated');
      socket.disconnect();
    };
  }, [agent]);

  const login = async (credentials: LoginRequest) => {
    const response = await api.login(credentials);
    const loginTime = Date.now();

    localStorage.setItem('token', response.token);
    localStorage.setItem('agent', JSON.stringify(response.agent));
    localStorage.setItem('lastLoginTime', loginTime.toString());

    console.log(`[Login] User logged in at ${formatSaoPauloTime(new Date(loginTime))}`);
    setAgent(response.agent);

    // Load auto-logout settings after successful login
    try {
      const settings = await api.getSettings();
      setAutoLogoutEnabled(settings.autoLogoutEnabled === 'true' || settings.autoLogoutEnabled === true);
      setAutoLogoutHour(parseInt(settings.autoLogoutHour || '20', 10));
    } catch (error) {
      console.error('Failed to load auto-logout settings after login:', error);
    }
  };

  const logout = async () => {
    // Mark as manual logout to prevent alert when WebSocket receives status update
    isManualLogout.current = true;

    try {
      // Call backend to set status to OFFLINE
      await api.logout();
    } catch (error) {
      // Even if the backend call fails, proceed with logout
      console.error('Failed to update status to OFFLINE on logout:', error);
    } finally {
      // Always clear local data and logout
      localStorage.removeItem('token');
      localStorage.removeItem('agent');
      localStorage.removeItem('lastLoginTime');
      setAgent(null);
    }
  };

  const updateAgent = (updatedAgent: Agent) => {
    setAgent(updatedAgent);
    localStorage.setItem('agent', JSON.stringify(updatedAgent));
  };

  const isAdmin = () => agent?.role === 'ADMIN';

  const isDepartmentManager = () => agent?.role === 'DEPARTMENT_MANAGER' || agent?.role === 'ADMIN';

  return (
    <AuthContext.Provider
      value={{
        agent,
        isAuthenticated: !!agent,
        isLoading,
        login,
        logout,
        updateAgent,
        isAdmin,
        isDepartmentManager,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
